import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, of, Subject, Subscription, takeUntil } from 'rxjs';
import { Facility, WorkflowItem, WorkflowBadgeStatus, WorkflowDecisionAction, StepStatus, WorkflowStatus } from '#models';
import { AuthService, EngineInboxResult, EngineInboxService, TelemetryService, ToasterService, WorkflowHeaderFacilityFilterService, WorkflowService } from '#services';
import { TelemetryEvent } from '#enums';
import { WorkflowSidebarComponent, WorkflowSidebarFilters } from './sidebar/workflow-sidebar.component';
import { WorkflowDetailShellComponent } from './detail/workflow-detail-shell/workflow-detail-shell.component';
import { exceptionTypeLabel } from './exception-type-label';
import { completedTimestamp, createdDate, createdTimestamp, reportDate } from './report-date';
import { CaseService } from '../../services/workflow/case.service';
import { WorkflowProgress } from '../../services/workflow-engine/engine-inbox.service';
import { WorkflowLoadFeedbackService } from './workflow-load-feedback.service';

type WorkflowSortOption = WorkflowSidebarFilters['sortOption'];
type InboxSource = 'workflow' | 'case' | 'engine';

const WORKFLOW_SORT_STORAGE_KEY = 'workflow-inbox-sort-option';

@Component({
  selector: 'app-workflow-inbox',
  imports: [WorkflowSidebarComponent, WorkflowDetailShellComponent],
  providers: [WorkflowLoadFeedbackService],
  templateUrl: './workflow-inbox.component.html',
  styleUrl: './workflow-inbox.component.scss',
})
export class WorkflowInboxComponent implements OnInit {
  private _workflowService = inject(WorkflowService);
  private _caseService = inject(CaseService);
  private _engineInbox = inject(EngineInboxService);
  private _authService = inject(AuthService);
  private _toasterService = inject(ToasterService);
  private _destroyRef = inject(DestroyRef);
  private _workflowHeaderFacilityFilter = inject(WorkflowHeaderFacilityFilterService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _telemetry = inject(TelemetryService);
  readonly loadFeedback = inject(WorkflowLoadFeedbackService);
  private _searchCancelled = new Subject<void>();
  private _detailRequest: Subscription | null = null;

  readonly WorkflowDecisionAction = WorkflowDecisionAction;

  private _allItems = signal<WorkflowItem[]>([]);
  private _rawItems = signal<WorkflowItem[]>([]);
  private _selectedItem = signal<WorkflowItem | null>(null);
  private _selectedTab = signal<string | null>(null);
  private _error = signal<boolean>(false);
  private _loading = signal<boolean>(false);
  private _caseLoading = signal<boolean>(false);
  private _shown = signal<boolean>(false);
  private _engineLoading = signal<boolean>(false);
  private _facilities = signal<Facility[]>([]);
  private _assets = signal<string[]>([]);
  private _selectedAsset = signal<string | null>(null);
  private _selectedSearchText = signal<string>('');
  private _selectedTimeline = signal<string>('all');
  private _selectedDate = signal<string | null>(null);
  private _selectedRunForDate = signal<string | null>(null);
  private _selectedExceptionType = signal<string | null>(null);
  private _selectedStatus = signal<string | null>(null);
  private _selectedWorkflowStatus = signal<WorkflowStatus | null>(null);
  private _selectedMyPendingActions = signal(false);
  private _selectedPriority = signal<string | null>(null);
  private _selectedSortOption = signal<WorkflowSortOption>('newest-date');
  private _facilitySelectionReady = false;
  private _lastFacilityId: string | null = null;
  private _searchRequestId = 0;
  private _failedSources = new Set<InboxSource>();
  private _pendingCaseId: string | null = null;
  private _lastSyncedCaseId: string | null = null;
  private _knownEngineLinks: { facilityId: string | null; links: Record<string, string> } | null = null;
  private _latestWorkflowProgress = new Map<string, WorkflowProgress>();
  // Undefined until a list is shown, so the first list never counts as a reload.
  private _shownFacilityId: string | null | undefined = undefined;

  allItems = this._allItems.asReadonly();
  rawItems = this._rawItems.asReadonly();
  selectedItem = this._selectedItem.asReadonly();
  selectedTab = this._selectedTab.asReadonly();
  error = computed(() => this._error() && this.loadFeedback.settled());
  loading = this._loading.asReadonly();
  /**
   * Only the first load of a facility replaces the inbox with a loading message. A reload keeps the open case mounted:
   * replacing it would abort an engine command still in flight and send the reader back to the summary tab.
   */
  baseLoading = computed(() => this._loading() || this._caseLoading());
  additionalLoading = computed(() => this.baseLoading() || this._engineLoading());
  initialLoading = computed(() => this.baseLoading() && !this._shown());
  /** True while engine cases are still joining a list that is already shown. */
  engineLoading = this._engineLoading.asReadonly();
  assets = this._assets.asReadonly();
  selectedAsset = this._selectedAsset.asReadonly();
  selectedSearchText = this._selectedSearchText.asReadonly();
  selectedTimeline = this._selectedTimeline.asReadonly();
  selectedDate = this._selectedDate.asReadonly();
  selectedRunForDate = this._selectedRunForDate.asReadonly();
  selectedExceptionType = this._selectedExceptionType.asReadonly();
  selectedStatus = this._selectedStatus.asReadonly();
  selectedWorkflowStatus = this._selectedWorkflowStatus.asReadonly();
  selectedMyPendingActions = this._selectedMyPendingActions.asReadonly();
  selectedPriority = this._selectedPriority.asReadonly();
  selectedSortOption = this._selectedSortOption.asReadonly();

  constructor() {
    this._engineInbox.stateRead$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({ caseId, badge, progress }) => {
        this._latestWorkflowProgress.set(caseId, { workflowBadge: badge, ...progress });
        const update = (items: WorkflowItem[]) => items.map(item =>
          (item.linkedEngineCaseId ?? item.engineCaseId ?? item.workflowCaseId) === caseId
            ? { ...item, workflowBadge: badge, ...progress }
            : item);
        this._rawItems.update(update);
        this._allItems.set(this._applyLocalFilters(
          this._rawItems(), this._selectedSearchText(), this._selectedTimeline(), this._selectedDate(),
          this._selectedExceptionType(), this._selectedStatus(), this._selectedPriority(), this._selectedSortOption(),
        ));
      });
    effect(() => {
      const selectedFacilityId = this._workflowHeaderFacilityFilter.selectedFacilityId();
      if (!this._facilitySelectionReady) {
        this._lastFacilityId = selectedFacilityId;
        return;
      }

      if (selectedFacilityId === this._lastFacilityId) {
        return;
      }

      this._lastFacilityId = selectedFacilityId;
      this.loadSearchResults();
    });
  }

  ngOnInit(): void {
    this._watchCaseDeepLink();
    this._loadSortPreference();
    this.loadFilters();
  }

  /**
   * Opens the case named by the `caseId` query param and switches to the `facilityId` query param,
   * both on load and on later URL changes.
   */
  private _watchCaseDeepLink(): void {
    this._route.queryParamMap
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(params => {
        this._selectedTab.set(params.get('tab'));
        const facilityId = params.get('facilityId');
        const caseId = params.get('caseId') ?? params.get('engineCaseId');

        // A URL facility change switches the inbox to that facility; the reloaded results then
        // resolve the pending case id (via the facility effect -> loadSearchResults).
        if (facilityId
          && facilityId !== this._workflowHeaderFacilityFilter.selectedFacilityId()
          && this._facilities().some(facility => facility.id === facilityId)) {
          this._pendingCaseId = caseId;
          this._workflowHeaderFacilityFilter.setSelectedFacilityId(facilityId);
          return;
        }

        if (!caseId) {
          return;
        }
        if (caseId === this._lastSyncedCaseId) {
          return; // our own URL write echoing back (also guards fast successive clicks)
        }
        this._pendingCaseId = caseId;
        // Wait for this case's source rather than resolving against a stale list.
        if (!this._isSourceLoading(this._sourceForCaseId(caseId))
          && !(this._sourceForCaseId(caseId) === 'case' && this._loading()) && this._allItems().length > 0) {
          this._applyPendingCaseSelection(this._allItems());
        }
      });
  }

  private _loadSortPreference(): void {
    try {
      const saved = localStorage.getItem(WORKFLOW_SORT_STORAGE_KEY);
      if (saved && this._isValidSortOption(saved)) {
        this._selectedSortOption.set(saved as WorkflowSortOption);
      }
    } catch {
      // localStorage might not be available in some contexts, silently ignore
    }
  }

  private _saveSortPreference(option: WorkflowSortOption): void {
    try {
      localStorage.setItem(WORKFLOW_SORT_STORAGE_KEY, option);
    } catch {
      // localStorage might not be available in some contexts, silently ignore
    }
  }

  private _isValidSortOption(value: string): value is WorkflowSortOption {
    return ['newest-date', 'oldest-date', 'newest-completed', 'oldest-completed', 'low-priority', 'high-priority'].includes(value);
  }

  private loadFilters(): void {
    this._error.set(false);
    this._loading.set(true);

    this.loadFeedback.track(forkJoin({
      assets:     this._workflowService.getAssets().pipe(catchError(() => of<string[]>([]))),
      facilities: this._workflowService.getFacilities().pipe(catchError(() => of<Facility[]>([]))),
    }))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ({ assets, facilities }) => {
          this._assets.set(assets);
          this._facilities.set(facilities);
          this._selectedAsset.set(assets.length > 1 ? null : (assets[0] ?? null));
          const urlFacilityId = this._route.snapshot.queryParamMap.get('facilityId');
          const facilityFromUrl = urlFacilityId && facilities.some(facility => facility.id === urlFacilityId)
            ? urlFacilityId
            : null;
          const defaultFacilityId = facilityFromUrl ?? facilities[0]?.id ?? null;
          this._workflowHeaderFacilityFilter.setFacilities(facilities);
          this._workflowHeaderFacilityFilter.setSelectedFacilityId(defaultFacilityId);
          this._lastFacilityId = defaultFacilityId;
          this._facilitySelectionReady = true;
          this.loadSearchResults();
        },
        error: () => {
          this._error.set(true);
          this._loading.set(false);
        },
      });
  }

  private loadSearchResults(): void {
    this._searchCancelled.next();
    this.loadFeedback.clearError('workflow-list');
    this.loadFeedback.clearError('case-list');
    this.loadFeedback.clearError('engine-list');
    this.loadFeedback.clearError('deep-link');
    this._loading.set(true);
    this._caseLoading.set(true);
    this._engineLoading.set(true);
    this._error.set(false);
    this._failedSources.clear();
    this._latestWorkflowProgress.clear();
    const requestId = ++this._searchRequestId;

    const asset      = this._selectedAsset();
    const facilityId = this._workflowHeaderFacilityFilter.selectedFacilityId();
    // Another facility's list is not a reload: show the loader rather than the previous facility's cases.
    if (facilityId !== this._shownFacilityId) {
      this._shown.set(false);
      this._cancelDetailRequest();
    }

    const engineCases$ = this._engineInbox.load(facilityId);

    let legacy: WorkflowItem[] | null = null;
    let cases: WorkflowItem[] | null = null;
    let engine: EngineInboxResult | null = null;

    const showResults = (source: InboxSource): void => {
      const items = [...(legacy ?? []), ...(cases ?? [])]
        .filter(item => this._normalized(item.displayReport) !== 'hide');
      this._error.set(!this.baseLoading() && this._failedSources.has('workflow') && this._failedSources.has('case'));
      if (this._error() || (this.baseLoading() && items.length === 0)) {
        return;
      }
      this._showResults(items, engine, source, facilityId);
    };

    this.loadFeedback.track(this._workflowService.searchWorkflows(asset, facilityId))
      .pipe(takeUntil(this._searchCancelled), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: items => {
          if (requestId !== this._searchRequestId) {
            return;
          }
          this._loading.set(false);
          legacy = items;
          showResults('workflow');
        },
        error: () => {
          if (requestId !== this._searchRequestId) {
            return;
          }
          this._loading.set(false);
          this._failedSources.add('workflow');
          this.loadFeedback.reportError('workflow-list', 'Legacy workflow cases could not be loaded.');
          legacy = [];
          showResults('workflow');
        },
      });

    this.loadFeedback.track(this._caseService.search(asset, facilityId))
      .pipe(takeUntil(this._searchCancelled), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: items => {
          if (requestId !== this._searchRequestId) {
            return;
          }
          this._caseLoading.set(false);
          cases = items;
          showResults('case');
        },
        error: () => {
          if (requestId !== this._searchRequestId) {
            return;
          }
          this._caseLoading.set(false);
          this._failedSources.add('case');
          this.loadFeedback.reportError('case-list', 'Well-exception cases could not be loaded.');
          cases = [];
          showResults('case');
        },
      });

    this.loadFeedback.track(engineCases$)
      .pipe(
        catchError(() => of<EngineInboxResult>({ items: [], links: {}, failed: true })),
        takeUntil(this._searchCancelled),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(result => {
        if (requestId !== this._searchRequestId) {
          return;
        }
        this._engineLoading.set(false);
        if (result.failed) {
          this._failedSources.add('engine');
          this.loadFeedback.reportError('engine-list', 'Workflow engine cases could not be loaded.');
        }
        this._knownEngineLinks = { facilityId, links: this._engineLinks(facilityId, result) };
        engine = result;
        showResults('engine');
      });
  }

  /**
   * Each source joins as soon as it answers, without waiting for another source to finish.
   * A legacy record whose Dossier opened an engine case carries that case. Engine cases are never listed on
   * their own: the workflow is part of its UC1 or UC2 case, so an engine case with no parent case has no card.
   */
  private _showResults(
    legacy: WorkflowItem[],
    engine: EngineInboxResult | null,
    source: InboxSource,
    facilityId: string | null,
  ): void {
    const links = this._engineLinks(facilityId, engine);
    const sameFacility = this._shownFacilityId === facilityId;
    this._shownFacilityId = facilityId;
    const engineItems = new Map(engine?.items.map(item => [item.id, item]) ?? []);
    const linkedLegacy = legacy.map(item => {
      const linkedEngineCaseId = links[item.id] ?? item.linkedEngineCaseId;
      const engineId = linkedEngineCaseId ?? item.engineCaseId ?? item.workflowCaseId;
      return {
        ...item,
        ...(linkedEngineCaseId ? { linkedEngineCaseId } : {}),
        workflowBadge: engineId ? engineItems.get(engineId)?.workflowBadge : undefined,
        workflowStatus: engineId ? engineItems.get(engineId)?.workflowStatus : undefined,
        workflowStepTitle: engineId ? engineItems.get(engineId)?.workflowStepTitle : undefined,
        workflowOutcome: engineId ? engineItems.get(engineId)?.workflowOutcome : undefined,
        workflowOwnerOid: engineId ? engineItems.get(engineId)?.workflowOwnerOid : undefined,
        ...(engineId ? this._latestWorkflowProgress.get(engineId) : {}),
      };
    });
    const merged = [...linkedLegacy];
    this._rawItems.set(merged);
    this._shown.set(true);
    const filteredItems = this._applyLocalFilters(
      merged,
      this._selectedSearchText(),
      this._selectedTimeline(),
      this._selectedDate(),
      this._selectedExceptionType(),
      this._selectedStatus(),
      this._selectedPriority(),
      this._selectedSortOption(),
    );
    this._allItems.set(filteredItems);

    // Missing engine metadata is not evidence that the open case no longer matches the workflow filter.
    if ((this._selectedWorkflowStatus() || this._selectedMyPendingActions()) && (this._engineLoading() || this._failedSources.has('engine'))) {
      return;
    }
    if (!this._pendingCaseId && sameFacility && this._keepSelection(filteredItems, source)) {
      return;
    }
    this._selectInitialItem(filteredItems);
  }

  /**
   * Keep the open case while its own list is pending or unavailable. Only that source's answer refreshes
   * its detail, so another source joining cannot interrupt the open view.
   */
  private _keepSelection(items: WorkflowItem[], source: InboxSource): boolean {
    const selected = this._selectedItem();
    if (!selected) {
      return false;
    }
    if (selected.engineCaseId && source === 'case') {
      const agentCase = items.find(item => item.linkedEngineCaseId === selected.engineCaseId && /^case-[1-9]\d*$/.test(item.id));
      if (agentCase) {
        this._selectedItem.set(agentCase);
        this.loadDetail(agentCase.id);
        this._syncCaseToUrl(agentCase);
        return true;
      }
    }
    const selectedSource: InboxSource = selected.engineCaseId ? 'engine' : /^case-[1-9]\d*$/.test(selected.id) ? 'case' : 'workflow';
    const listed = items.some(item => item.id === selected.id
      || (!!selected.engineCaseId && item.linkedEngineCaseId === selected.engineCaseId));
    if (!listed && !this._isSourceLoading(selectedSource) && !this._failedSources.has(selectedSource)) {
      return false;
    }
    this._carryEngineLink();
    if (listed && source === selectedSource && selectedSource !== 'engine') {
      this.loadDetail(selected.id);
    }
    return true;
  }

  private _sourceForCaseId(caseId: string): InboxSource {
    const normalized = this._normalized(caseId);
    return /^(?:case-)?[1-9]\d*$/.test(normalized) ? 'case' : 'engine';
  }

  private _isSourceLoading(source: InboxSource): boolean {
    return source === 'workflow' ? this._loading() : source === 'case' ? this._caseLoading() : this._engineLoading();
  }

  /**
   * The legacy-record links to engine cases for the facility. A Dossier's link never changes, but the inbox only
   * reports it once the engine has answered for that case, so a reload keeps every link an earlier answer gave:
   * dropping one, while the reload waits or because one engine read failed, would tear down an open engine view
   * and abort any command it still has in flight.
   */
  private _engineLinks(facilityId: string | null, engine: EngineInboxResult | null): Record<string, string> {
    const known = this._knownEngineLinks?.facilityId === facilityId ? this._knownEngineLinks.links : {};
    return { ...known, ...(engine?.links ?? {}) };
  }

  /** Gives the open case the engine link its list row now carries, keeping the detail already loaded for it. */
  private _carryEngineLink(): void {
    const selected = this._selectedItem();
    const linked = selected ? this._rawItems().find(item => item.id === selected.id)?.linkedEngineCaseId : null;
    if (selected && linked && selected.linkedEngineCaseId !== linked) {
      this._selectedItem.set({ ...selected, linkedEngineCaseId: linked });
    }
  }

  loadWorkflows(): void {
    this.loadFilters();
  }

  onItemSelected(item: WorkflowItem): void {
    if (this._selectedItem()?.id !== item.id) {
      this._selectedTab.set(null);
    }
    this._selectedItem.set(item); // show immediately, steps populate on detail response
    this.loadDetail(item.id);
    this._syncCaseToUrl(item);
  }

  onTabSelected(tab: string): void {
    if (this._selectedTab() === tab) return;
    this._selectedTab.set(tab);
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  /** Selects a pending deep-linked case when present, otherwise the first item in the list. */
  private _selectInitialItem(items: WorkflowItem[]): void {
    if (this._applyPendingCaseSelection(items)) {
      return;
    }
    if (items[0]) {
      this._selectedItem.set(items[0]);
      this.loadDetail(items[0].id);
      this._syncCaseToUrl(items[0]);
    } else {
      this._cancelDetailRequest();
      this._selectedItem.set(null);
      this._syncCaseToUrl(null);
    }
  }

  /**
   * Selects the case named by a pending `caseId` (the workflow id) within the selected facility.
   * When the case isn't in that facility the user is forwarded to the first available case.
   * Returns true while a pending id controls selection, including while its source is unavailable.
   */
  private _applyPendingCaseSelection(items: WorkflowItem[]): boolean {
    const caseId = this._pendingCaseId;
    if (!caseId) {
      return false;
    }
    const match = items.find(item => this._matchesCaseId(item, caseId));
    const source = this._sourceForCaseId(caseId);
    // An engine case only opens through its parent UC1/UC2 case, which any source may still be bringing.
    const waiting = source === 'engine'
      ? this.additionalLoading()
      : this._isSourceLoading(source) || (source === 'case' && this._loading());
    const failed = source === 'engine'
      ? this._failedSources.size > 0
      : this._failedSources.has(source);
    if (!match && (waiting || failed)) {
      this._cancelDetailRequest();
      this._selectedItem.set(null);
      return true;
    }
    this._pendingCaseId = null;
    if (match) {
      this._selectedItem.set(match);
      this.loadDetail(match.id);
      this._syncCaseToUrl(match);
    } else {
      this._forwardToFirstAvailableCase(caseId, items);
    }
    return true;
  }

  private _matchesCaseId(item: WorkflowItem, caseId: string): boolean {
    const requested = this._normalized(caseId);
    return this._normalized(item.id) === requested
      || (this._normalized(item.id) === `case-${requested}`)
      || (!!item.linkedEngineCaseId && this._normalized(item.linkedEngineCaseId) === requested);
  }

  /**
   * A deep-linked case that is not in the selected facility (missing or belonging to another
   * facility) is unavailable: notify the user and forward to the first available case in the facility.
   */
  private _forwardToFirstAvailableCase(caseId: string, items: WorkflowItem[]): void {
    // Workflow ids are integers; avoid echoing arbitrary query text into the message.
    const label = /^\d+$/.test(caseId) ? `Case Id ${caseId}` : 'The requested case';
    this.loadFeedback.reportError('deep-link', `${label} isn't available.`);
    const first = items[0] ?? null;
    this._selectedItem.set(first);
    if (first) {
      this.loadDetail(first.id);
    } else {
      this._cancelDetailRequest();
    }
    this._syncCaseToUrl(first);
  }

  /** Keeps the facilityId and caseId query params in sync with the open case so the URL is shareable. */
  private _syncCaseToUrl(item: WorkflowItem | null): void {
    // Keep the selected facility in the URL even when no case is open.
    const facilityId = this._workflowHeaderFacilityFilter.selectedFacilityId();
    const engineCaseId = item?.engineCaseId ?? null;
    const caseId = engineCaseId ? null : item?.id.match(/^case-([1-9]\d*)$/)?.[1] ?? item?.id ?? null;
    this._lastSyncedCaseId = caseId ?? engineCaseId;
    const queryParams: Record<string, string | null> = { facilityId, caseId, engineCaseId };
    if (this._selectedTab()) queryParams['tab'] = this._selectedTab();
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams,
      replaceUrl: true,
    });
  }

  private _cancelDetailRequest(): void {
    this._detailRequest?.unsubscribe();
    this._detailRequest = null;
    this.loadFeedback.clearError('case-detail');
  }

  private loadDetail(id: string): void {
    this._cancelDetailRequest();
    if (this._rawItems().some(item => item.id === id && item.engineCaseId)) {
      return; // The engine case view reads its own state; /api/workflow knows nothing of this id.
    }

    // Keep the basic item visible on detail failure and report it here with a toast and retry message.
    this._detailRequest = this.loadFeedback.track(this._workflowService.getItemDetail(id))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: detail => {
          if (this._selectedItem()?.id !== id) return;
          // The detail endpoint knows nothing of the engine; keep the link the list row carries.
          const linked = this._rawItems().find(item => item.id === id)?.linkedEngineCaseId;
          this._selectedItem.set(linked ? { ...detail, linkedEngineCaseId: linked } : detail);
        },
        error: () => {
          if (this._selectedItem()?.id !== id) return;
          this.loadFeedback.reportError('case-detail', 'The case details could not be loaded. Select the case again to retry.');
          this._selectedItem.update(item => item ? { ...item, caseDetailError: 'Case details could not be loaded. Select the case again to retry.' } : null);
        },
      });
  }

  onAssetChanged(asset: string | null): void {
    this._selectedAsset.set(asset);
    this.loadSearchResults();
  }

  onTimelineChanged(timeline: string): void {
    const normalized = this._normalizeTimeline(timeline);
    this._selectedTimeline.set(normalized);
    this.loadSearchResults();
  }

  onFiltersChanged(filters: WorkflowSidebarFilters): void {
    this._selectedSearchText.set(filters.searchText.trim());
    this._selectedAsset.set(filters.asset);
    this._selectedTimeline.set(this._normalizeTimeline(filters.timeline));
    this._selectedDate.set(this._normalizeNullableFilter(filters.date));
    this._selectedRunForDate.set(this._normalizeNullableFilter(filters.runForDate));
    this._selectedExceptionType.set(this._normalizeNullableFilter(filters.exceptionType));
    this._selectedStatus.set(this._normalizeStatus(filters.status));
    this._selectedWorkflowStatus.set(filters.workflowStatus ?? null);
    this._selectedMyPendingActions.set(filters.myPendingActions ?? false);
    this._selectedPriority.set(this._normalizeNullableFilter(filters.priority));
    this._selectedSortOption.set(filters.sortOption);
    this._saveSortPreference(filters.sortOption);
    this.loadSearchResults();
    // Only the names of the filters in use are reported, never their values (search text and asset names are free text).
    const filterKeys = Object.entries(filters)
      .filter(([key, value]) => key !== 'sortOption' && !!value && value !== 'all')
      .map(([key]) => key)
      .sort()
      .join(',');
    this._telemetry.trackAppEvent(TelemetryEvent.CaseListFiltered, { filterKeys });
  }

  /**
   * Records an Approve/Reject decision on the selected workflow's active step. The UI updates
   * optimistically so the decision (who/when/what) shows immediately; the server response then
   * reconciles the state. On error the previous state is restored. On Reject & Escalate the
   * reviewer comment (`agentFeedback`) is sent with the decision and saved as Agent Feedback.
   */
  onDecision(action: WorkflowDecisionAction, agentFeedback?: string): void {
    const current = this._selectedItem();
    if (!current || /^case-[1-9]\d*$/.test(current.id)) {
      return;
    }

    const account = this._authService.getAccount();
    const name = account?.name?.trim() || this._authService.getAccountEmail() || 'Unknown';
    const email = this._authService.getAccountEmail() || null;

    const previous = current;
    this.setItem(this.applyDecision(current, action, name, email));

    this._workflowService.recordDecision(current.id, action, agentFeedback)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: server => {
          this._telemetry.trackAppEvent(TelemetryEvent.CaseDecisionSubmitted, { workflowId: current.id, outcome: action });
          if (server) {
            this.setItem(server);
          }

        },
        error: error => {
          this._telemetry.trackCommandFailed('record_decision', error, { workflowId: current.id });
          this.setItem(previous);
        },
      });
  }

  private setItem(item: WorkflowItem): void {
    this._selectedItem.set(item);
    this._allItems.update(items => items.map(i => (i.id === item.id ? item : i)));
  }

  /** Returns a copy of the item with the decision stamped on its first not-yet-completed step. */
  private applyDecision(
    item: WorkflowItem,
    action: WorkflowDecisionAction,
    name: string,
    email: string | null,
  ): WorkflowItem {
    const nowIso = new Date().toISOString();
    let decided = false;
    const steps = item.steps.map(step => {
      if (!decided && !step.completedOn && step.status !== StepStatus.Done) {
        decided = true;
        return {
          ...step,
          status: StepStatus.Done,
          action,
          completedBy: name,
          completedByEmail: email,
          completedOn: nowIso,
        };
      }
      return step;
    });
    return { ...item, steps, badge: WorkflowBadgeStatus.Closed };
  }

  private _applyLocalFilters(
    items: WorkflowItem[],
    searchText: string,
    timeline: string,
    date: string | null,
    exceptionType: string | null,
    status: string | null,
    priority: string | null,
    sortOption: WorkflowSortOption,
  ): WorkflowItem[] {
    let filtered = items;
    if (this._selectedMyPendingActions()) {
      const viewerOid = this._authService.getObjectId()?.trim().toLowerCase();
      filtered = filtered.filter(item => !!viewerOid
        && !!(item.linkedEngineCaseId || item.engineCaseId || item.workflowCaseId)
        && item.workflowStatus !== 'done' && item.workflowBadge !== WorkflowBadgeStatus.Closed
        && item.workflowOwnerOid?.trim().toLowerCase() === viewerOid);
    }
    const workflowStatus = this._selectedWorkflowStatus();
    if (workflowStatus) {
      filtered = filtered.filter(item => !!(item.linkedEngineCaseId || item.engineCaseId || item.workflowCaseId)
        && item.workflowStatus === workflowStatus);
    } else {
      // A closed case stays listed while it is deep-linked or open, so a notification link still lands on it.
      const pendingCaseId = this._pendingCaseId;
      const selectedId = this._selectedItem()?.id;
      filtered = filtered.filter(item => item.workflowStatus !== 'done' || item.id === selectedId
        || (!!pendingCaseId && this._matchesCaseId(item, pendingCaseId)));
    }

    const text = searchText.trim().toLowerCase();
    if (text.length > 0) {
      filtered = filtered.filter(item => {
        const haystack = [
          item.exceptionReferenceId ?? '',
          item.title,
          item.description,
          item.asset ?? '',
        ].join(' ').toLowerCase();
        return haystack.includes(text);
      });
    }

    const normalizedType = this._normalizeNullableFilter(exceptionType);
    if (normalizedType) {
      filtered = filtered.filter(item => this._normalized(this._exceptionTypeLabel(item)) === this._normalized(normalizedType));
    }

    const normalizedStatus = this._normalizeStatus(status);
    if (normalizedStatus) {
      filtered = filtered.filter(item => this._statusFilterValue(item) === normalizedStatus);
    }

    const normalizedPriority = this._normalizeNullableFilter(priority);
    if (normalizedPriority) {
      filtered = filtered.filter(item => this._normalized(item.priority) === this._normalized(normalizedPriority));
    }

    if (date) {
      filtered = filtered.filter(item => createdDate(item) === date);
    }

    const runForDate = this._selectedRunForDate();
    if (runForDate) {
      filtered = filtered.filter(item => reportDate(item) === runForDate);
    }

    return this._sortItems(this._filterByTimeline(filtered, timeline), sortOption);
  }

  private _sortItems(items: WorkflowItem[], sortOption: WorkflowSortOption): WorkflowItem[] {
    if (sortOption === 'newest-completed' || sortOption === 'oldest-completed') {
      return [...items].sort((left, right) => {
        const leftTime = completedTimestamp(left);
        const rightTime = completedTimestamp(right);
        if (leftTime === null && rightTime !== null) return 1;
        if (rightTime === null && leftTime !== null) return -1;
        const dateDiff = leftTime === null || rightTime === null ? 0
          : sortOption === 'oldest-completed' ? leftTime - rightTime : rightTime - leftTime;
        return dateDiff || this._normalized(left.id).localeCompare(this._normalized(right.id));
      });
    }

    if (sortOption === 'oldest-date') {
      return [...items].sort((left, right) => {
        const dateDiff = this._compareRunForDate(left, right, true);
        if (dateDiff !== 0) {
          return dateDiff;
        }

        return this._normalized(left.id).localeCompare(this._normalized(right.id));
      });
    }

    if (sortOption === 'low-priority') {
      return [...items].sort((left, right) => {
        const priorityDiff = this._priorityRankLowFirst(left.priority) - this._priorityRankLowFirst(right.priority);
        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        const dateDiff = this._compareRunForDate(left, right);
        if (dateDiff !== 0) {
          return dateDiff;
        }

        return this._normalized(left.id).localeCompare(this._normalized(right.id));
      });
    }

    if (sortOption === 'high-priority') {
      return [...items].sort((left, right) => {
        const priorityDiff = this._priorityRankHighFirst(left.priority) - this._priorityRankHighFirst(right.priority);
        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        const dateDiff = this._compareRunForDate(left, right);
        if (dateDiff !== 0) {
          return dateDiff;
        }

        return this._normalized(left.id).localeCompare(this._normalized(right.id));
      });
    }

    // Default: newest-date
    return [...items].sort((left, right) => {
      const dateDiff = this._compareRunForDate(left, right);
      if (dateDiff !== 0) {
        return dateDiff;
      }

      return this._normalized(left.id).localeCompare(this._normalized(right.id));
    });
  }

  private _priorityRankHighFirst(priority: string | null | undefined): number {
    const normalized = this._normalized(priority);
    if (normalized === 'high') {
      return 0;
    }

    if (normalized === 'medium') {
      return 1;
    }

    if (normalized === 'low') {
      return 2;
    }

    return 3;
  }

  private _priorityRankLowFirst(priority: string | null | undefined): number {
    const normalized = this._normalized(priority);
    if (normalized === 'low') {
      return 0;
    }

    if (normalized === 'medium') {
      return 1;
    }

    if (normalized === 'high') {
      return 2;
    }

    return 3;
  }

  private _exceptionTypeLabel(item: WorkflowItem): string {
    return exceptionTypeLabel(item);
  }

  private _statusFilterValue(item: WorkflowItem): string {
    const normalized = this._normalized(item.badge || item.status);
    if (normalized === 'awaitingdecision' || normalized === 'awaiting decision') {
      return 'awaiting decision';
    }

    if (normalized === 'closed') {
      return 'closed';
    }

    return 'new';
  }

  private _filterByTimeline(items: WorkflowItem[], timeline: string): WorkflowItem[] {
    if (timeline === 'all') {
      return items;
    }

    const now = Date.now();
    const cutoff = now - this._timelineWindowMs(timeline);
    return items.filter(item => {
      const created = createdTimestamp(item);
      return created !== null && created >= cutoff && created <= now;
    });
  }

  private _timelineWindowMs(timeline: string): number {
    switch (timeline) {
      case '1w':
        return 7 * 24 * 60 * 60 * 1000;
      case '2w':
        return 14 * 24 * 60 * 60 * 1000;
      case '1m':
        return 30 * 24 * 60 * 60 * 1000;
      default:
        return Number.MAX_SAFE_INTEGER;
    }
  }

  private _compareRunForDate(left: WorkflowItem, right: WorkflowItem, oldestFirst = false): number {
    const leftDate = reportDate(left);
    const rightDate = reportDate(right);
    if (leftDate === null) return rightDate === null ? 0 : 1;
    if (rightDate === null) return -1;
    return oldestFirst ? leftDate.localeCompare(rightDate) : rightDate.localeCompare(leftDate);
  }

  private _normalizeTimeline(value: string | null | undefined): string {
    const normalized = value?.trim().toLowerCase() ?? 'all';
    return normalized === '1w' || normalized === '2w' || normalized === '1m' || normalized === 'all'
      ? normalized
      : 'all';
  }

  private _normalizeStatus(value: string | null | undefined): string | null {
    const normalized = this._normalized(value);
    if (normalized === 'new' || normalized === 'awaiting decision' || normalized === 'closed') {
      return normalized;
    }

    return null;
  }

  private _normalizeNullableFilter(value: string | null | undefined): string | null {
    const normalized = value?.trim() ?? '';
    return normalized.length > 0 ? normalized : null;
  }

  private _normalized(value?: string | null): string {
    return value?.trim().toLowerCase() ?? '';
  }
}
